import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  ACCESS_SUPERPOWERED_SEARCH,
  ADD_TO_CHROME,
  GET_ACCESS_TO_SEARCH,
  ITS_FREE,
} from '../../../languages/en/search';
import ThemeV2 from '../../../theme';
import SimpleModal from '../../atoms/Modal';
import Link from '../../atoms/Link_V2';
import SVGIcon from '../../atoms/SVGIcon';
import Body from '../../atoms/Body';
import Button from '../../atoms/Button';
import { useFeatureSplit } from '../../../hooks/useFeatureSplit';
import {
  SplitNames,
  TreatmentTypes,
} from '../../../hooks/useSplitSdkConfig/constants';
import {
  isChrome,
  isDesktopPlatform,
  isEdge,
  isMacOS,
} from '../../../Utils/window';
import { getChromeExtensionId } from '../../../Utils/extension';
import {
  InnerModal,
  ModalBackground,
  PrimaryHeaderWrapper,
  LogoContainer,
  SearchBar,
  SearchBarContainer,
  ShortcutContainer,
  StyledLogo,
  StyledSVGIcon,
  ShortcutButtonKey,
  ShortcutKey,
  CustomHeader,
  HamburgerButton,
} from './styles';
import { PrimaryHeaderProps } from './interface';
import { microsoftTeamsStore } from '../../../stores/microsoftStore';
import { trackEvent } from '../../../Utils/analytics';
import {
  AnalyticsEvents,
  HEADER_ANALYTICS_EVENTS,
  HEADER_EVENT,
} from '../../../Utils/analytics/constants';

const useStyles = makeStyles({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '8px',
    maxWidth: '560px',
  },
  modalBody: {
    paddingTop: '4px',
    paddingBottom: '24px',
  },
});

const PrimaryHeader = (props: PrimaryHeaderProps) => {
  const { path, companyName, companyLogo, toggleLeftAsideOpen, children } =
    props;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [shortcut, setShortcut] = useState<string>();
  const classes = useStyles();
  const { treatment } = useFeatureSplit(SplitNames.Search);
  const shouldDisplaySearch =
    treatment === TreatmentTypes.ON &&
    (isChrome || isEdge) &&
    isDesktopPlatform;
  const extensionId = getChromeExtensionId();

  const isLoggedInThroughTeamsMobile =
    microsoftTeamsStore.getState().isLoggedInThroughMobileTeamsApp;

  const openModal = () => {
    if (extensionId) {
      chrome.runtime.sendMessage(extensionId, { type: 'searchModal:show' });
    } else {
      setIsModalOpen(true);
    }
    trackEvent(HEADER_EVENT, undefined, {
      action: HEADER_ANALYTICS_EVENTS.SEARCH_CLICKED,
      event: AnalyticsEvents.ACTION,
    });
  };

  const openShortcutPage = () => {
    if (extensionId) {
      chrome.runtime.sendMessage(extensionId, {
        type: 'chromePage:open',
        data: { url: 'chrome://extensions/shortcuts' },
      });
    }
  };

  const goToChromeStore = () => {
    window.open(
      'https://chrome.google.com/webstore/detail/assembly-universal-workpl/lfifhhbadciimoimgencbhbkdmaigfhn',
    );
    trackEvent(HEADER_EVENT, undefined, {
      action: HEADER_ANALYTICS_EVENTS.ADD_TO_BROWSER_CLICKED,
      event: AnalyticsEvents.ACTION,
    });
  };

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.shiftKey && ev.code === 'Space') {
        if ((isMacOS && ev.metaKey) || (!isMacOS && ev.ctrlKey)) {
          setIsModalOpen(true);
        }
      }
    };

    if (!extensionId) {
      document.addEventListener('keydown', onKeyDown);

      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    } else {
      chrome.runtime.sendMessage(
        extensionId,
        { type: 'shortcut:get' },
        (res) => {
          setShortcut(res.shortcut);
        },
      );
    }
  }, [extensionId]);

  const displayKeys = () => {
    switch (shortcut) {
      case '':
        return (
          <ShortcutButtonKey onClick={openShortcutPage}>
            Set Shortcut
          </ShortcutButtonKey>
        );
      case '⇧⌘Space': // macOS chrome
      case '⇧⌘Space bar': // macOS edge
      case 'Ctrl + Shift + Space': // windows
      case undefined: // no extension installed, unsupported
        return (
          <>
            <ShortcutKey>{isMacOS ? '⌘ cmd' : 'ctrl'}</ShortcutKey>
            <ShortcutKey>shift</ShortcutKey>
            <ShortcutKey>space</ShortcutKey>
          </>
        );
      default:
        return <ShortcutKey>{shortcut}</ShortcutKey>;
    }
  };

  return (
    <>
      <PrimaryHeaderWrapper>
        <div style={{ display: 'flex', flexGrow: '1' }}>
          {!isLoggedInThroughTeamsMobile && (
            <HamburgerButton onClick={toggleLeftAsideOpen}>
              <SVGIcon icon="hamburger" size="39px" />
            </HamburgerButton>
          )}
          <Link to={path}>
            <div style={{ display: 'flex', marginLeft: '12px' }}>
              <LogoContainer>
                <StyledLogo src={companyLogo} alt={companyName} />
              </LogoContainer>
              <CustomHeader>{companyName}</CustomHeader>
            </div>
          </Link>
          {shouldDisplaySearch && (
            <SearchBarContainer>
              <StyledSVGIcon
                icon="search"
                color={ThemeV2.palette.gray9}
                size="16px"
              />
              <SearchBar value="Search" onClick={openModal} type="button" />
              <ShortcutContainer shortcut={shortcut}>
                {displayKeys()}
              </ShortcutContainer>
            </SearchBarContainer>
          )}
        </div>
        <div style={{ display: 'flex' }}>{children}</div>
      </PrimaryHeaderWrapper>
      <SimpleModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        className={classes.modal}
      >
        <ModalBackground />
        <InnerModal>
          <Body variant="subHead1" style={{ fontWeight: '500' }}>
            {ACCESS_SUPERPOWERED_SEARCH} 🚀
          </Body>
          <Body variant="body1" className={classes.modalBody}>
            {GET_ACCESS_TO_SEARCH}
          </Body>
          <Button isFullWidth onClick={goToChromeStore}>
            {ADD_TO_CHROME} &mdash; {ITS_FREE}
          </Button>
        </InnerModal>
      </SimpleModal>
    </>
  );
};

export default PrimaryHeader;
